import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { useAuth } from "../lib/AuthContext";
import { buildLoginUrl } from "../lib/AuthContext";
import { fetchMyItineraries, deleteMyItinerary } from "../lib/api";
import { ArrowRight, MapPin, Trash, Sparkle, SignIn, NotePencil } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function MyTrips() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (loading || !user) return;
    setFetching(true);
    fetchMyItineraries()
      .then(setItems)
      .catch(() => toast.error("Couldn't load your trips."))
      .finally(() => setFetching(false));
  }, [user, loading]);

  const remove = async (id) => {
    if (!confirm("Delete this saved trip?")) return;
    try {
      await deleteMyItinerary(id);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Trip removed");
    } catch {
      toast.error("Could not delete the trip.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF7F0]">
      <Seo
        title="My Trips · Flycher"
        description="Your saved Flycher AI travel itineraries, all in one place."
        noindex
      />
      <Navbar />
      <main className="flex-1 max-w-5xl mx-auto w-full px-5 sm:px-8 pt-28 pb-20">
        <span className="kicker">— Your saved trips</span>
        <h1 className="font-serif text-4xl sm:text-5xl mt-2 text-slate-900">
          My Trips
        </h1>
        <p className="text-slate-600 mt-3 max-w-xl">
          Every itinerary you create signed-in lands here. Reopen, refine with the AI co-pilot, or download a fresh PDF anytime.
        </p>

        {loading && (
          <div className="mt-12 text-slate-500 font-mono text-sm">Loading…</div>
        )}

        {!loading && !user && (
          <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <div className="w-12 h-12 mx-auto rounded-2xl grid place-items-center" style={{ background: "#EAF4FB" }}>
              <Sparkle size={22} color="#2A7FB8" weight="duotone" />
            </div>
            <h2 className="font-serif text-2xl mt-4 text-slate-900">
              Sign in to see your saved trips
            </h2>
            <p className="text-sm text-slate-600 mt-2">
              One click with Google — every itinerary you create will auto-save here so you can revisit anytime.
            </p>
            <a
              href={buildLoginUrl("/me/trips")}
              data-testid="mytrips-signin-cta"
              className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-full text-sm font-semibold text-white shadow-md shadow-sky-200/60"
              style={{ background: "#2A7FB8" }}
            >
              <SignIn size={16} weight="bold" /> Continue with Google
            </a>
          </div>
        )}

        {!loading && user && !fetching && items.length === 0 && (
          <div className="mt-10 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <p className="text-slate-600">
              You haven't generated any trips yet.
            </p>
            <Link
              to="/itinerary"
              className="inline-flex items-center gap-2 mt-5 px-5 py-3 rounded-full text-sm font-semibold text-white"
              style={{ background: "#FF7E4D" }}
            >
              <Sparkle size={16} weight="fill" /> Plan your first trip
            </Link>
          </div>
        )}

        {user && items.length > 0 && (
          <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="mytrips-list">
            {items.map((t) => (
              <article
                key={t.id}
                data-testid={`mytrip-${t.id}`}
                className="rounded-2xl bg-white border border-slate-200 hover:border-[#2A7FB8] transition-colors p-5 flex flex-col"
              >
                <span className="kicker text-[10px]">— {t.country}</span>
                <h3 className="font-serif text-xl mt-1 text-slate-900 leading-snug line-clamp-2">
                  {t.title}
                </h3>
                <div className="mt-2 text-xs font-mono text-slate-500 flex items-center gap-1">
                  <MapPin size={12} /> {t.dates}
                </div>
                {t.estimated_cost_inr && (
                  <div className="mt-2 text-sm font-semibold text-[#2A7FB8]">
                    {t.estimated_cost_inr}
                  </div>
                )}
                <p className="text-sm text-slate-600 mt-3 line-clamp-2">
                  {t.summary}
                </p>
                <div className="mt-auto pt-4 flex items-center justify-between">
                  <Link
                    to={`/trip/${t.id}`}
                    data-testid={`mytrip-open-${t.id}`}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2A7FB8] hover:underline"
                  >
                    Open trip <ArrowRight size={14} weight="bold" />
                  </Link>
                  <div className="flex items-center gap-1">
                    <Link
                      to={`/me/notes?trip=${t.id}&new=1`}
                      data-testid={`mytrip-addnote-${t.id}`}
                      className="text-[#FF7E4D] hover:bg-[#FFF1E8] transition-colors p-1.5 rounded-full"
                      aria-label="Add a travel note"
                      title="Add a travel note"
                    >
                      <NotePencil size={16} weight="duotone" />
                    </Link>
                    <button
                      onClick={() => remove(t.id)}
                      data-testid={`mytrip-delete-${t.id}`}
                      className="text-slate-400 hover:text-rose-600 transition-colors p-1.5"
                      aria-label="Delete trip"
                      type="button"
                    >
                      <Trash size={16} weight="duotone" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
